/**
 * One answer to "what does this cook want tonight".
 *
 * Before this module the answer depended on which button was pressed. The
 * onboarding week honoured allergies; « Régénérer » honoured none of them and
 * only read the three taste axes; « Remplacer » honoured allergies, the pantry
 * and the personal rating; the Recettes screen honoured the declared diets and
 * the excluded ingredients; and the profile the digest learns from the cook's
 * own behaviour reached exactly one generator, on a browser window between 800
 * and 1180px. A cook who declared a lactose allergy during onboarding got a
 * dairy-free week and could bring the dairy straight back with one tap on
 * « Régénérer ».
 *
 * Everything the cook has ever told us now resolves here, in two kinds:
 *
 * - **Hard** — allergies, intolerances, excluded ingredients, declared diets.
 *   A recipe that fails one is never proposed, whatever else it has going for
 *   it. A slot that cannot be filled stays empty and says so.
 * - **Soft** — the three onboarding axes, the digest's learned preferences, the
 *   household's rating, what is already in the fridge. These rank the survivors.
 *
 * The axes are the prior (three duels, answered before the app knew anything)
 * and the digest refines it (dozens of small gestures afterwards). They are
 * summed rather than arbitrated: they answer different questions, and the
 * digest prompt is given the axes so it refines them instead of contradicting.
 */
import { calculateTasteProfileBonus } from './tastePreferenceMatching';
import { activeRows, pantryMatcherForNames, shelfMatcher, type MatchableItem } from './pantryMatch';
import { recipeHasAllergen, normalizeAllergenText } from './allergenMatcher';
import { axesToTargets, tasteAxesBonus, type TasteAxes } from './tasteAxes';
import { satisfiesDietaryRestrictions } from '../utils/dietClassifier';
import type { PersonalRecipeIndexEntry } from './personalRecipeIndex';
import type { TasteProfile } from '../redux/feedbackSlice';

/**
 * The structural minimum every candidate carries. `RecipeIndexEntry` and
 * `PersonalRecipeIndexEntry` both satisfy it.
 */
export interface PreferenceRecipe {
  id: string;
  title: string;
  ingredients: string[];
  readyInMinutes: number;
  handsOnMinutes?: number;
  diets?: string[];
  cuisines?: string[];
  dishTypes?: string[];
  sourceRating?: number;
}

/** Only the rating is read here; verdicts already derive into it. */
export interface RatingSignal {
  rating?: number;
}

export interface CookPreferenceInput {
  /** The three onboarding axes, or null before onboarding. */
  axes?: TasteAxes | null;
  /** What the digest learned from the cook's gestures. */
  tasteProfile?: TasteProfile | null;
  /** Allergens + custom allergens, already through `buildAllergenTerms`. */
  allergenTerms?: string[];
  /** Raw disliked-ingredient lines from Réglages. */
  dislikedIngredients?: string[];
  /** Declared diets from Réglages (vegetarian, gluten-free…). */
  dietaryRestrictions?: string[];
  /** recipeId → { rating } (recipeMetadata; the verdicts derive into it). */
  metadataById?: Record<string, RatingSignal>;
  /** Names of active pantry items, as stored (the server's path). */
  pantryNames?: string[];
  /**
   * The pantry rows themselves, when the caller holds them: the matcher is
   * then the shelf's own (`shelfMatcher`), shared with every ranking that reads
   * the same array, so the library is walked once per pantry state and not
   * once more for the score (#865). Wins over `pantryNames`.
   */
  pantry?: readonly MatchableItem[];
}

export interface CookPreferences {
  /** Allergens and excluded ingredients, normalised into one term list. */
  excludedTerms: string[];
  /** True when the recipe breaks a hard rule and must never be proposed. */
  isExcluded: (recipe: PreferenceRecipe) => boolean;
  /** Soft weekly veg/fish/meat distribution, or null with no axes. */
  targets: { fish: number; veg: number; meat: number } | null;
  /** Additive ranking score; higher is better. Only ranks, never excludes. */
  score: (recipe: PreferenceRecipe, opts?: { isWeekend?: boolean }) => number;
  /** True when at least one hard rule is in force (for logs and copy). */
  hasHardRules: boolean;
}

/**
 * How a 1-5 rating moves a candidate. A five is worth about as much as being
 * on the cook's preferred axis (+80); a one has to sink the dish without the
 * -1000 finality of an allergen, because a rating is an opinion, not a rule.
 */
const RATING_SCORE: Record<number, number> = { 5: 45, 4: 20, 3: 0, 2: -35, 1: -70 };

/** Each pantry ingredient already on the shelf, up to five of them. */
const PANTRY_HIT_SCORE = 8;
const PANTRY_HIT_CAP = 5;

/** A recipe with no known time cannot be judged on the time axis. */
const UNKNOWN_TIME_SCORE = -5;

export function resolveCookPreferences(input: CookPreferenceInput): CookPreferences {
  const {
    axes = null,
    tasteProfile = null,
    allergenTerms = [],
    dislikedIngredients = [],
    dietaryRestrictions = [],
    metadataById = {},
    pantryNames = [],
    pantry,
  } = input;

  // Allergens and disliked ingredients share one word-anchored matcher, so
  // "lait" does not remove every "laitue" and "oeuf" leaves "boeuf" alone.
  const excludedTerms = [
    ...allergenTerms,
    ...dislikedIngredients.map(normalizeAllergenText).filter(Boolean),
  ];

  // One verdict per recipe object for the resolver's life, like `score` below
  // (#865). Keyed on the object, not the id: a hard rule has to be judged
  // again for a recipe whose content was replaced under the same id (an edit
  // that added an allergen), and the index entries are long-lived objects.
  const excludedCache = new WeakMap<object, boolean>();
  const isExcluded = (recipe: PreferenceRecipe): boolean => {
    const cached = excludedCache.get(recipe);
    if (cached !== undefined) return cached;
    const value =
      (excludedTerms.length > 0 && recipeHasAllergen(recipe, excludedTerms)) ||
      (dietaryRestrictions.length > 0 && !satisfiesDietaryRestrictions(recipe, dietaryRestrictions));
    excludedCache.set(recipe, value);
    return value;
  };

  // One matcher for the resolver's life (the index is built once), and one
  // count per recipe however many slots ask for it (#668). "Uses" is the
  // recipe screen's answer (`pantryMatch.ts`), so a French shelf scores an
  // English recipe and "lait" does not score "laitue".
  const shelf = pantry ? shelfMatcher(activeRows(pantry)) : pantryMatcherForNames(pantryNames);
  const pantryCache = new Map<string, number>();
  const hitsOf = (recipe: PreferenceRecipe): number => {
    if (shelf.isEmpty) return 0;
    const cached = pantryCache.get(recipe.id);
    if (cached !== undefined) return cached;
    const value = shelf.itemsUsedBy(recipe).length;
    pantryCache.set(recipe.id, value);
    return value;
  };

  // One score per recipe for the resolver's life (#865): « Remplacer » asks
  // it about the whole library on every opening and on every echo while the
  // sheet is open, and the taste-profile match alone was 130 ms a pass on a
  // throttled phone. The resolver is rebuilt whenever an input changes.
  const scoreCache = new WeakMap<object, [weekday?: number, weekend?: number]>();
  const score = (recipe: PreferenceRecipe, opts?: { isWeekend?: boolean }): number => {
    const slot = opts?.isWeekend ? 1 : 0;
    let entry = scoreCache.get(recipe);
    if (!entry) {
      entry = [];
      scoreCache.set(recipe, entry);
    }
    const cached = entry[slot];
    if (cached !== undefined) return cached;
    const value = computeScore(recipe, opts);
    entry[slot] = value;
    return value;
  };
  const computeScore = (recipe: PreferenceRecipe, opts?: { isWeekend?: boolean }): number => {
    let total = 0;
    if (axes) {
      total += tasteAxesBonus(
        {
          title: recipe.title,
          ingredients: recipe.ingredients,
          readyInMinutes: recipe.readyInMinutes,
          handsOnMinutes: recipe.handsOnMinutes,
          diets: recipe.diets,
        },
        axes,
        opts?.isWeekend ?? false,
      );
    }
    if (tasteProfile) {
      total += calculateTasteProfileBonus(recipe as unknown as PersonalRecipeIndexEntry, tasteProfile);
    }
    const rating = metadataById[recipe.id]?.rating;
    if (rating != null) total += RATING_SCORE[Math.round(rating)] ?? 0;
    total += Math.min(hitsOf(recipe), PANTRY_HIT_CAP) * PANTRY_HIT_SCORE;
    if (!recipe.readyInMinutes) total += UNKNOWN_TIME_SCORE;
    return total;
  };

  return {
    excludedTerms,
    isExcluded,
    targets: axes ? axesToTargets(axes) : null,
    score,
    hasHardRules: excludedTerms.length > 0 || dietaryRestrictions.length > 0,
  };
}
